import { useState } from 'react';
import { Mail, Phone, MapPin, MessageCircle } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Textarea } from '@/components/ui/textarea';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import { toast } from 'sonner';
import axios from 'axios';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const ContactPage = () => {
  const [loading, setLoading] = useState(false);
  const [form, setForm] = useState({
    name: '',
    email: '',
    subject: '',
    message: '',
    type: 'general'
  });

  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);
    try {
      await axios.post(`${API}/contact`, null, { params: form });
      toast.success('Message sent! We\'ll get back to you soon.');
      setForm({ name: '', email: '', subject: '', message: '', type: 'general' });
    } catch (error) {
      toast.error('Failed to send message. Please try again.');
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="pt-32 pb-24" data-testid="contact-page">
      <div className="chaleur-container">
        <div className="max-w-3xl mx-auto text-center mb-16">
          <p className="caption text-primary mb-4">Get in Touch</p>
          <h1 className="text-4xl md:text-5xl font-light mb-6">We'd Love to Hear From You</h1>
          <p className="text-muted-foreground">
            Questions about an order? Need styling advice? Want to collaborate? 
            We're here to help.
          </p>
        </div>

        <div className="grid lg:grid-cols-3 gap-12">
          {/* Contact Info */}
          <div className="space-y-8">
            <div className="p-6 bg-secondary/30 border border-border">
              <Mail className="w-6 h-6 text-primary mb-4" />
              <h3 className="font-medium mb-2">Email Us</h3>
              <p className="text-sm text-muted-foreground mb-2">For general inquiries</p>
              <a href="mailto:sabinakamal@gmail.com" className="text-primary hover:underline">
                sabinakamal@gmail.com
              </a>
            </div>
            <div className="p-6 bg-secondary/30 border border-border">
              <Phone className="w-6 h-6 text-primary mb-4" />
              <h3 className="font-medium mb-2">Call Us</h3>
              <p className="text-sm text-muted-foreground mb-2">Sabina Kamal</p>
              <a href="tel:+918796244314" className="text-primary hover:underline">
                +91 87962 44314
              </a>
            </div>
            <div className="p-6 bg-secondary/30 border border-border">
              <MessageCircle className="w-6 h-6 text-primary mb-4" />
              <h3 className="font-medium mb-2">WhatsApp</h3>
              <p className="text-sm text-muted-foreground mb-2">Quick assistance</p>
              <a href="https://wa.me/918796244314" target="_blank" rel="noopener noreferrer" className="text-primary hover:underline">
                +91 87962 44314
              </a>
            </div>
          </div>

          {/* Contact Form */}
          <div className="lg:col-span-2">
            <form onSubmit={handleSubmit} className="space-y-6">
              <div className="grid sm:grid-cols-2 gap-6">
                <div>
                  <Label htmlFor="name" className="text-sm mb-2 block">Name</Label>
                  <Input
                    id="name"
                    value={form.name}
                    onChange={(e) => setForm({ ...form, name: e.target.value })}
                    className="input-chaleur"
                    required
                  />
                </div>
                <div>
                  <Label htmlFor="email" className="text-sm mb-2 block">Email</Label>
                  <Input
                    id="email"
                    type="email"
                    value={form.email}
                    onChange={(e) => setForm({ ...form, email: e.target.value })}
                    className="input-chaleur"
                    required
                  />
                </div>
              </div>

              <div className="grid sm:grid-cols-2 gap-6">
                <div>
                  <Label htmlFor="type" className="text-sm mb-2 block">Inquiry Type</Label>
                  <Select value={form.type} onValueChange={(v) => setForm({ ...form, type: v })}>
                    <SelectTrigger className="rounded-none">
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent className="rounded-none">
                      <SelectItem value="general">General Inquiry</SelectItem>
                      <SelectItem value="support">Order Support</SelectItem>
                      <SelectItem value="collaboration">Collaboration</SelectItem>
                      <SelectItem value="press">Press</SelectItem>
                    </SelectContent>
                  </Select>
                </div>
                <div>
                  <Label htmlFor="subject" className="text-sm mb-2 block">Subject</Label>
                  <Input
                    id="subject"
                    value={form.subject}
                    onChange={(e) => setForm({ ...form, subject: e.target.value })}
                    className="input-chaleur"
                    required
                  />
                </div>
              </div>

              <div>
                <Label htmlFor="message" className="text-sm mb-2 block">Message</Label>
                <Textarea
                  id="message"
                  value={form.message}
                  onChange={(e) => setForm({ ...form, message: e.target.value })}
                  className="min-h-[150px] rounded-none border-border focus:border-primary"
                  required
                />
              </div>

              <Button type="submit" className="btn-chaleur-primary" disabled={loading}>
                {loading ? 'Sending...' : 'Send Message'}
              </Button>
            </form>
          </div>
        </div>
      </div>
    </div>
  );
};

export default ContactPage;
